import type { AssetResultMatch, AssetResultMatchTypename } from '../types';

const EXCLUDED_MATCHES = ['NameMatch'];

// Grouped MatchType constants ordered by priority, highest to lowest
// Order is set by product/design; DO NOT SORT ALPHABETICALLY

const ESSENTIAL_MATCHES: AssetResultMatchTypename[] = [
  'StatusMatch',
  'AssigneeMatch',
  'UploadedAtMatch',
  'CreatorMatch', // aka Uploader
  'CommentCountMatch',
  'RatingMatch',
  'KeywordsMatch',
  'NotesMatch',
];

const PREFERRED_MATCHES_1: AssetResultMatchTypename[] = ['VisualMatch'];

const PREFERRED_MATCHES_2: AssetResultMatchTypename[] = ['CommentMatch'];

const PREFERRED_MATCHES_3: AssetResultMatchTypename[] = [
  'TranscriptionMatch',
  'TranscriptMatch', // stubbed type
  'TranscriptionResultMatch', // stubbed type
];

const OTHER_MATCHES: AssetResultMatchTypename[] = [
  'FileTypeMatch',
  'FormatMatch',
  'FilesizeMatch',
  'SourceFilenameMatch',
  'DurationMatch',
  'BitRateMatch',
  'CodecMatch',
  'ResolutionWidthMatch',
  'ResolutionHeightMatch',
  'BitDepthMatch',
  'FrameRateMatch',
  'ColorSpaceMatch',
  'DynamicRangeMatch',
  'HasAlphaChannelMatch',
  'AudioSampleRateMatch',
  'PageCountMatch',
];

// Unclaimed match types (default to OTHER_MATCHES, lowest priority) include:
// DeletedAtMatch, ChannelsMatch, InsertedAtMatch, ProjectMatch, UpdatedAtMatch

const SORT_ORDER: Record<'essential' | 'other', Record<string, number>> = {
  essential: Object.fromEntries(
    ESSENTIAL_MATCHES.map((type, index) => [type, index])
  ),
  other: Object.fromEntries(OTHER_MATCHES.map((type, index) => [type, index])),
};

const sortMatchGroup = (
  group: keyof typeof SORT_ORDER,
  matches: AssetResultMatch[]
) => {
  const order = SORT_ORDER[group];
  return matches.sort((a, b) => {
    const aIndex = a.__typename
      ? order[a.__typename] ?? Number.MAX_SAFE_INTEGER
      : Number.MAX_SAFE_INTEGER;
    const bIndex = b.__typename
      ? order[b.__typename] ?? Number.MAX_SAFE_INTEGER
      : Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
};

const sortRotation = (matchGroups: AssetResultMatch[][]) => {
  const maxLength = Math.max(...matchGroups.map((group) => group.length));
  const rotatedMatches: AssetResultMatch[] = [];

  for (let i = 0; i < maxLength; i += 1) {
    for (const group of matchGroups) {
      if (i < group.length) {
        rotatedMatches.push(group[i]);
      }
    }
  }

  return rotatedMatches;
};

/**
 * Orders an asset's search-result matches by filtering, bucketing, sorting,
 * and interleaving them:
 *
 * 1. Filters out matches with no `__typename` and any in `EXCLUDED_MATCHES`.
 * 2. Buckets the remaining matches into essential, preferred (1/2/3), and
 *    other groups based on typename membership.
 * 3. Sorts the 'essential' and 'other' groups internally by their declared
 *    priority order (`ESSENTIAL_MATCHES` / `OTHER_MATCHES`); unrecognized
 *    typenames sort last.
 * 4. Round-robin interleaves the three 'preferred' groups together, then
 *    round-robin interleaves essential, preferred, and other — rather than
 *    concatenating tiers — so e.g. essential[0], preferred[0], other[0],
 *    essential[1], preferred[1], other[1], ... until shorter groups are
 *    exhausted and the remainder of the longest group is appended in order.
 */
export const getOrderedMatches = (matches: AssetResultMatch[]) => {
  const filteredMatches = matches?.filter(
    (match) =>
      match && match.__typename && !EXCLUDED_MATCHES.includes(match.__typename)
  );
  const matchGroups = filteredMatches.reduce<{
    essentialMatches: AssetResultMatch[];
    otherMatches: AssetResultMatch[];
    preferredMatches1: AssetResultMatch[];
    preferredMatches2: AssetResultMatch[];
    preferredMatches3: AssetResultMatch[];
  }>(
    (acc, match) => {
      if (ESSENTIAL_MATCHES.includes(match.__typename)) {
        acc.essentialMatches.push(match);
      } else if (PREFERRED_MATCHES_1.includes(match.__typename)) {
        acc.preferredMatches1.push(match);
      } else if (PREFERRED_MATCHES_2.includes(match.__typename)) {
        acc.preferredMatches2.push(match);
      } else if (PREFERRED_MATCHES_3.includes(match.__typename)) {
        acc.preferredMatches3.push(match);
      } else {
        acc.otherMatches.push(match);
      }
      return acc;
    },
    {
      essentialMatches: [] as AssetResultMatch[],
      otherMatches: [] as AssetResultMatch[],
      preferredMatches1: [] as AssetResultMatch[],
      preferredMatches2: [] as AssetResultMatch[],
      preferredMatches3: [] as AssetResultMatch[],
    }
  );
  const essentialMatches = sortMatchGroup(
    'essential',
    matchGroups.essentialMatches
  );
  const otherMatches = sortMatchGroup('other', matchGroups.otherMatches);
  const preferredMatches = sortRotation([
    matchGroups.preferredMatches1,
    matchGroups.preferredMatches2,
    matchGroups.preferredMatches3,
  ]);

  return sortRotation([essentialMatches, preferredMatches, otherMatches]);
};
